import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import {
  User,
  Right,
  RightEntity,
  RightState,
} from '../../services/definitions.service';
import { DataModelService } from '../../services/data-model.service';
import {
  NotiBottomSheetComponent,
  NotiSheetData,
  NotiType,
} from '../../noti-bottom-sheet/noti-bottom-sheet.component';

/**
 * Represents a loaded right with its Firestore document path
 * so we can update/delete it directly.
 */
interface RightEntry extends Right {
  path?: string;
}

/**
 * All available rights that can be assigned, derived from RightEntity enum.
 */
interface RightOption {
  id: number;
  label: string;
}

@Component({
  selector: 'app-manage-user-rights-dialog',
  templateUrl: './manage-user-rights-dialog.component.html',
  styleUrls: ['./manage-user-rights-dialog.component.css'],
})
export class ManageUserRightsDialogComponent implements OnInit {
  allUsers: User[] = [];
  selectedUser: User | null = null;
  rights: RightEntry[] = [];
  isLoadingUsers: boolean = true;
  isLoadingRights: boolean = false;
  processingPaths: Set<string> = new Set();
  isAdding: boolean = false;

  /** Available right options derived from RightEntity enum */
  allRightOptions: RightOption[] = [];

  /** State options for the edit dropdown */
  stateOptions = [
    { value: RightState.ALLOW, label: 'Erlaubt' },
    { value: RightState.DECLINE, label: 'Abgelehnt' },
    { value: RightState.INHERIT, label: 'Vererbt' },
  ];

  /** New right form fields */
  newRightId: number | null = null;
  newRightState: RightState = RightState.ALLOW;

  constructor(
    public dialogRef: MatDialogRef<ManageUserRightsDialogComponent>,
    private dataModel: DataModelService,
    private bottomSheet: MatBottomSheet
  ) {}

  ngOnInit(): void {
    this.buildRightOptions();
    this.loadUsers();
  }

  /**
   * Builds the list of assignable rights from the RightEntity enum.
   */
  private buildRightOptions(): void {
    this.allRightOptions = Object.keys(RightEntity)
      .filter((key) => isNaN(Number(key)))
      .map((key) => ({
        id: RightEntity[key] as number,
        label: key,
      }))
      .sort((a, b) => a.id - b.id);
  }

  /**
   * Loads all users sorted by name.
   */
  async loadUsers(): Promise<void> {
    this.isLoadingUsers = true;
    try {
      const users = await this.dataModel.loadAllUsers(false, true);
      if (Array.isArray(users)) {
        this.allUsers = (users as User[]).sort((a, b) => {
          const nameA = this.getUserDisplayName(a).toLowerCase();
          const nameB = this.getUserDisplayName(b).toLowerCase();
          return nameA.localeCompare(nameB);
        });
      }
    } catch {
      this.allUsers = [];
    }
    this.isLoadingUsers = false;
  }

  /**
   * Returns a display name for a user.
   */
  getUserDisplayName(user: User): string {
    if (!user) {
      return 'Unbekannt';
    }
    const first = user.personalData?.firstName || '';
    const last = user.personalData?.lastName || '';
    const full = `${first} ${last}`.trim();
    return full || user.displayName || user.email || 'Unbekannt';
  }

  /**
   * Called when a user is selected from the dropdown.
   */
  async onUserSelected(): Promise<void> {
    if (!this.selectedUser) {
      this.rights = [];
      return;
    }
    this.isAdding = false;
    await this.loadRights();
  }

  /**
   * Loads rights for the selected user.
   */
  async loadRights(): Promise<void> {
    if (!this.selectedUser) {
      return;
    }

    this.isLoadingRights = true;
    this.rights = [];

    const result = await this.dataModel.getUserSpecificRights(
      this.selectedUser.uid,
      true
    );

    if (result.result && Array.isArray(result.data)) {
      this.rights = result.data.map((raw: any) => ({
        id: raw.id,
        name: raw.name || '',
        state: raw.state || RightState.UNDEFINED,
        path: raw.path,
      }));
      this.rights.sort((a, b) => a.id - b.id);
    }

    this.isLoadingRights = false;
  }

  /**
   * Returns the label for a RightEntity id.
   */
  getRightLabel(id: number): string {
    const option = this.allRightOptions.find((o) => o.id === id);
    return option ? option.label : `Recht #${id}`;
  }

  /**
   * Returns the state label.
   */
  getStateLabel(state: string): string {
    const option = this.stateOptions.find((o) => o.value === state);
    return option ? option.label : state;
  }

  /**
   * Changes the state of an existing right.
   */
  async onChangeState(right: RightEntry, newState: RightState): Promise<void> {
    if (!right.path || this.isProcessing(right)) {
      return;
    }

    this.processingPaths.add(right.path);

    const result = await this.dataModel.updateUserRight(right.path, {
      state: newState,
    });

    if (result.result) {
      right.state = newState;
    } else {
      this.showError('Status konnte nicht geändert werden.');
    }

    this.processingPaths.delete(right.path);
  }

  /**
   * Deletes a right after confirmation.
   */
  async onDeleteRight(right: RightEntry): Promise<void> {
    if (!right.path || this.isProcessing(right)) {
      return;
    }

    const sheetRef = this.bottomSheet.open(NotiBottomSheetComponent, {
      data: new NotiSheetData(
        `Recht "${this.getRightLabel(right.id)}" wirklich entfernen?`,
        NotiType.Warn,
        0,
        true,
        true,
        'Entfernen',
        'Abbrechen'
      ),
      panelClass: 'noti-bottom-sheet-info',
      disableClose: false,
    });

    sheetRef.afterDismissed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.processingPaths.add(right.path);

      const result = await this.dataModel.deleteUserRight(right.path);

      if (result.result) {
        this.rights = this.rights.filter((r) => r.path !== right.path);
      } else {
        this.showError('Recht konnte nicht gelöscht werden.');
      }

      this.processingPaths.delete(right.path);
    });
  }

  /**
   * Shows the add-right form.
   */
  onShowAddForm(): void {
    this.isAdding = true;
    this.newRightId = null;
    this.newRightState = RightState.ALLOW;
  }

  /**
   * Cancels adding a new right.
   */
  onCancelAdd(): void {
    this.isAdding = false;
  }

  /**
   * Returns the list of rights not yet assigned to this user.
   */
  get availableRightOptions(): RightOption[] {
    const existingIds = new Set(this.rights.map((r) => r.id));
    return this.allRightOptions.filter((o) => !existingIds.has(o.id));
  }

  /**
   * Adds a new right to the selected user.
   */
  async onAddRight(): Promise<void> {
    if (!this.selectedUser || this.newRightId == null) {
      return;
    }

    const rightData: Right = {
      id: this.newRightId,
      name: this.getRightLabel(this.newRightId),
      state: this.newRightState,
    };

    const result = await this.dataModel.addUserRight(
      this.selectedUser.uid,
      rightData
    );

    if (result.result) {
      this.isAdding = false;
      await this.loadRights();
    } else {
      this.showError('Recht konnte nicht hinzugefügt werden.');
    }
  }

  isProcessing(right: RightEntry): boolean {
    return this.processingPaths.has(right.path);
  }

  onClose(): void {
    this.dialogRef.close();
  }

  private showError(message: string): void {
    this.bottomSheet.open(NotiBottomSheetComponent, {
      data: new NotiSheetData(message, NotiType.Error, 3000, false, false),
      panelClass: 'noti-bottom-sheet-info',
      disableClose: false,
    });
  }
}
